import { useCallback, useRef, useState } from 'react';

/**
 * Wraps an async handler so it can't run again until the previous call settles.
 * The ref blocks re-entry immediately (a fast double click lands before React re-renders);
 * `busy` is for disabling buttons.
 */
export function useSingleFlight() {
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    <Args extends unknown[], R>(fn: (...args: Args) => R | Promise<R>) =>
      async (...args: Args): Promise<R | undefined> => {
        if (inFlight.current) return undefined;
        inFlight.current = true;
        setBusy(true);
        try {
          return await fn(...args);
        } finally {
          inFlight.current = false;
          setBusy(false);
        }
      },
    [],
  );

  return { run, busy };
}
